import { useEffect, useState } from "react";
import { History } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { api } from "../lib/api";

const ROLE_LABELS = {
  professional: "Professional", teacher: "Teacher", parent: "Family",
  school_admin: "School admin",
};

// Parent-facing "who accessed my child's data" timeline, built from the
// append-only audit ledger (read-only, professionals/teachers/family members).
export const AccessLogPanel = ({ child }) => {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);

  useEffect(() => {
    if (open) {
      api.get(`/children/${child.child_id}/access-log`)
        .then(({ data }) => setEvents(data.events))
        .catch(() => setEvents([]));
    }
  }, [open, child.child_id]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full rounded-lg gap-2 text-muted-foreground" data-testid={`access-log-${child.child_id}`}>
          <History className="h-4 w-4" aria-hidden="true" /> Access log
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="access-log-dialog">
        <DialogHeader>
          <DialogTitle>Who accessed {child.display_name}'s data</DialogTitle>
          <DialogDescription>
            Every view or change by professionals, teachers and family members is recorded in a
            tamper-proof ledger. This list can never be edited or deleted.
          </DialogDescription>
        </DialogHeader>
        {!events ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="access-log-empty">
            No outside access yet — only you have seen {child.display_name}'s data.
          </p>
        ) : (
          <div className="space-y-2" data-testid="access-log-list">
            {events.map((e, i) => (
              <div key={i} className="flex items-start justify-between gap-3 rounded-xl bg-accent px-4 py-3" data-testid={`access-log-row-${i}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{e.label}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {e.is_you ? "You" : e.actor_name} · {new Date(e.ts).toLocaleString()}
                  </p>
                </div>
                <Badge variant="secondary" className="shrink-0">{ROLE_LABELS[e.actor_role] || e.actor_role}</Badge>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
